import api, { getErrorMessage } from './api';

const teamsService = {
  /**
   * Get teams for current user's organization
   * @returns {Promise<Array>} - Teams list
   */
  getMyTeams: async () => {
    try {
      const response = await api.get('/api/v1/teams/');
      return response.data;
    } catch (error) {
      throw new Error(getErrorMessage(error));
    }
  },

  /**
   * Get IDs of teams where the current user is an active team_admin.
   * @returns {Promise<Array<string>>} - Team IDs
   */
  getMyAdminTeamIds: async () => {
    try {
      const response = await api.get('/api/v1/teams/my-admin-team-ids');
      return response.data || [];
    } catch (error) {
      throw new Error(getErrorMessage(error));
    }
  },

  /**
   * Get all teams (admin only)
   * Note: Admin team CRUD is not available. Uses user endpoint instead.
   * @param {string} organizationId - Optional organization ID to filter by
   * @returns {Promise<Array>} - Teams list
   */
  getAllTeams: async (organizationId = null) => {
    try {
      // Use the user teams endpoint since admin CRUD is not available
      const response = await api.get('/api/v1/teams/');
      return response.data;
    } catch (error) {
      throw new Error(getErrorMessage(error));
    }
  },

  /**
   * Get a specific team by ID (admin only)
   * Note: Admin team CRUD is not available. Returns team from list.
   * @param {string} teamId - Team ID
   * @returns {Promise<object>} - Team details
   */
  getTeam: async (teamId) => {
    try {
      // Fetch all teams and find the one with matching ID
      const response = await api.get('/api/v1/teams/');
      const teams = response.data || [];
      const team = teams.find(t => t.id === teamId);
      if (!team) {
        throw new Error('Team not found');
      }
      return team;
    } catch (error) {
      throw new Error(getErrorMessage(error));
    }
  },

  /**
   * Create a new team (admin only)
   * Note: Direct team creation is not available. Use batch-create instead.
   * @param {object} teamData - Team data
   * @returns {Promise<object>} - Created team
   */
  createTeam: async (teamData) => {
    try {
      const response = await api.post('/api/v1/teams/', teamData);
      return response.data;
    } catch (error) {
      throw new Error(getErrorMessage(error));
    }
  },

  /**
   * Update a team (admin only)
   * Note: Team update is not available in the current backend.
   * @param {string} teamId - Team ID
   * @param {object} teamData - Team data to update
   * @returns {Promise<object>} - Updated team
   */
  updateTeam: async (teamId, teamData) => {
    try {
      const response = await api.put(`/api/v1/teams/${teamId}`, teamData);
      return response.data;
    } catch (error) {
      throw new Error(getErrorMessage(error));
    }
  },

  /**
   * Delete a team (admin only)
   * Note: Team deletion is not available in the current backend.
   * @param {string} teamId - Team ID
   * @returns {Promise<object>} - Success response
   */
  deleteTeam: async (teamId) => {
    try {
      const response = await api.delete(`/api/v1/teams/${teamId}`);
      return response.data;
    } catch (error) {
      throw new Error(getErrorMessage(error));
    }
  },

  /**
   * Get team members
   * @param {string} teamId - Team ID
   * @returns {Promise<Array>} - Team members list
   */
  getTeamMembers: async (teamId) => {
    try {
      const response = await api.get(`/api/v1/teams/${teamId}/members`);
      return response.data;
    } catch (error) {
      throw new Error(getErrorMessage(error));
    }
  },

  /**
   * Add a member to a team (admin only)
   * Note: Direct member management is not available. Use batch-create.
   * @param {string} teamId - Team ID
   * @param {string} userId - User ID to add
   * @param {string} role - Role (member, lead, team_admin)
   * @returns {Promise<object>} - Team membership
   */
  addTeamMember: async (teamId, userId, role = 'member') => {
    try {
      const response = await api.post(`/api/v1/teams/${teamId}/members`, {
        user_id: userId,
        role,
      });
      return response.data;
    } catch (error) {
      throw new Error(getErrorMessage(error));
    }
  },

  /**
   * Remove a member from a team (admin only)
   * Note: Direct member management is not available.
   * @param {string} teamId - Team ID
   * @param {string} userId - User ID to remove
   * @returns {Promise<object>} - Success response
   */
  removeTeamMember: async (teamId, userId) => {
    try {
      const response = await api.delete(`/api/v1/teams/${teamId}/members/${userId}`);
      return response.data;
    } catch (error) {
      throw new Error(getErrorMessage(error));
    }
  },

  /**
   * Assign team admin
   * @param {string} teamId - Team ID
   * @param {string} userId - User ID to make admin
   * @returns {Promise<object>} - Success response
   */
  assignTeamAdmin: async (teamId, userId) => {
    try {
      const response = await api.post(`/api/v1/teams/${teamId}/admins`, {
        user_id: userId,
      });
      return response.data;
    } catch (error) {
      throw new Error(getErrorMessage(error));
    }
  },

  /**
   * Get available membership fields for batch team creation (admin only)
   * @returns {Promise<Array>} - Available fields list
   */
  getMembershipFields: async () => {
    try {
      const response = await api.get('/api/v1/admin/teams/membership-fields');
      return response.data;
    } catch (error) {
      throw new Error(getErrorMessage(error));
    }
  },

  /**
   * Batch create teams based on user attributes (admin only)
   * @param {object} batchData - Batch creation data
   * @param {string} batchData.organization_id - Organization ID
   * @param {Array<string>} batchData.fields - Fields to group by
   * @param {string} batchData.operator - AND or OR logic
   * @param {boolean} batchData.include_missing_values - Include users with missing values
   * @param {boolean} batchData.dry_run - Preview mode (don't create)
   * @returns {Promise<object>} - Batch creation result or preview
   */
  batchCreateTeams: async (batchData) => {
    try {
      const response = await api.post('/api/v1/admin/teams/batch-create', batchData);
      return response.data;
    } catch (error) {
      throw new Error(getErrorMessage(error));
    }
  },

  /**
   * Get budget information for all teams the current user is a member of
   * @returns {Promise<Array>} - Teams with budget info
   */
  getMyTeamBudgets: async () => {
    try {
      const response = await api.get('/api/v1/teams/my-budgets');
      return response.data;
    } catch (error) {
      throw new Error(getErrorMessage(error));
    }
  },

  /**
   * Get budget information for a specific team
   * @param {string} teamId - Team ID
   * @returns {Promise<object>} - Team budget info
   */
  getTeamBudget: async (teamId) => {
    try {
      const response = await api.get(`/api/v1/teams/${teamId}/budget`);
      return response.data;
    } catch (error) {
      throw new Error(getErrorMessage(error));
    }
  },

  // Alias methods for compatibility with BudgetsSection component
  list: async () => teamsService.getAllTeams(),
};

// Alias for compatibility
teamsService.list = teamsService.getAllTeams;
teamsService.listTeams = teamsService.getAllTeams;

export default teamsService;
